import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { consultationService } from '../services/consultationService'
import { templateService } from '../services/templateService'
import { patientService } from '../services/patientService'
import { patientHistoryService } from '../services/patientHistoryService'
import { Consultation, ConsultationCode, ConsultationTerminologySystem, NoteHighlight } from '../types/consultation'
import { TemplateCategory } from '../types/wordTemplate'
import { ConsultationArtefactList } from '../components/ConsultationArtefactList'
import { ConsultationWorkspaceTabs } from '../components/ConsultationWorkspaceTabs'
import { GenerateDocumentModal } from '../components/GenerateDocumentModal'
import { Modal } from '../components/Modal'
import { MedicationsCard } from '../components/MedicationsCard'
import { VitalSignsForm } from '../components/VitalSignsForm'
import { VitalSignsChartsDialog } from '../components/VitalSignsChartsDialog'
import { VitalSignsTableDialog } from '../components/VitalSignsTableDialog'
import { vitalSignsService } from '../services/vitalSignsService'
import { VitalSignsFormData } from '../types/vitalSigns'
import { getUnitPreference } from '../utils/userPreferences'
import { formatDate } from '../utils/locale'
import { AISuggestion } from '../services/aiService'
import { useGeminiContext } from '../contexts/GeminiContextContext'
import { buildConsultationContextWithNotes } from '../utils/aiContextUtils'
import { terminologyService, TerminologySearchResult } from '../services/terminologyService'
import { authService } from '../services/authService'
import './ConsultationWorkspace.css'
import { ConsultationNoteEditor } from '../components/ConsultationNoteEditor'

export function ConsultationWorkspace() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout>()
  const reasonSaveTimeoutRef = useRef<NodeJS.Timeout>()
  const timerIntervalRef = useRef<NodeJS.Timeout>()
  const initializedConsultationIdRef = useRef<string | null>(null)
  const lastSavedReasonRef = useRef<string>('')
  const { setConsultationContext } = useGeminiContext()

  const [notes, setNotes] = useState('')
  const [noteHighlights, setNoteHighlights] = useState<NoteHighlight[]>([])
  const [diagnosis, setDiagnosis] = useState<string[]>([])
  const [codes, setCodes] = useState<ConsultationCode[]>([])
  const [selectedSystem, setSelectedSystem] = useState<ConsultationTerminologySystem>('ICPC2')
  const [codeSearch, setCodeSearch] = useState('')
  const [codeResults, setCodeResults] = useState<TerminologySearchResult[]>([])
  const [isSearchingCodes, setIsSearchingCodes] = useState(false)
  const [plan, setPlan] = useState('')
  const [reason, setReason] = useState('')
  const [patientInstructions, setPatientInstructions] = useState('')
  const [instructionsGiven, setInstructionsGiven] = useState(false)
  const [instructionsGivenAt, setInstructionsGivenAt] = useState<string>('')
  const [instructionsGivenBy, setInstructionsGivenBy] = useState<string>('')
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory | 'all'>('all')
  const [isSaving, setIsSaving] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [isTimerPaused, setIsTimerPaused] = useState(false)
  const [, setPausedAt] = useState<number | null>(null)
  const [showVitalsForm, setShowVitalsForm] = useState(false)
  const [showVitalsCharts, setShowVitalsCharts] = useState(false)
  const [showVitalsTable, setShowVitalsTable] = useState(false)

  const { data: consultation, isLoading, error } = useQuery({
    queryKey: ['consultation', id],
    queryFn: () => consultationService.getById(id!),
    enabled: !!id,
  })

  const { data: patient } = useQuery({
    queryKey: ['patient', consultation?.patientId],
    queryFn: () => patientService.getById(consultation!.patientId),
    enabled: !!consultation?.patientId,
  })

  // Fetch existing conditions to check which codes are already added
  const { data: clinicalHistory } = useQuery({
    queryKey: ['patient-clinical-history', consultation?.patientId],
    queryFn: () => patientHistoryService.getClinicalHistory(consultation!.patientId),
    enabled: !!consultation?.patientId,
  })

  // Create a map to track which codes are added as conditions
  const conditionCodeMap = useMemo(() => {
    const map = new Map<string, string>()
    if (clinicalHistory?.conditions) {
      clinicalHistory.conditions.forEach((condition) => {
        const normalizedName = condition.condition.toLowerCase().trim()
        map.set(normalizedName, condition.id)
        if (condition.icd10Code) {
          map.set(`ICD10:${condition.icd10Code}`, condition.id)
        }
      })
    }
    return map
  }, [clinicalHistory])

  // Check if a code is already added as a condition
  const isCodeAddedAsCondition = (code: ConsultationCode): string | null => {
    const normalizedDisplay = code.display.toLowerCase().trim()
    if (conditionCodeMap.has(normalizedDisplay)) {
      return conditionCodeMap.get(normalizedDisplay) || null
    }
    return null
  }

  const { data: templates } = useQuery({
    queryKey: ['templates', selectedCategory],
    queryFn: () => templateService.getAll(selectedCategory === 'all' ? undefined : selectedCategory),
  })

  // Fetch all consultations for the patient to show past documents
  const { data: allConsultations } = useQuery({
    queryKey: ['consultations', 'patient', consultation?.patientId],
    queryFn: () => consultationService.getByPatient(consultation!.patientId),
    enabled: !!consultation?.patientId,
  })

  // Fetch latest vitals for header display - aggregates latest value for each vital type
  const { data: latestVitals, refetch: refetchLatestVitals } = useQuery({
    queryKey: ['vital-signs', 'patient', consultation?.patientId, 'latest'],
    queryFn: async () => {
      if (!consultation?.patientId) return null
      const allVitals = await vitalSignsService.getByPatient(consultation.patientId)
      if (allVitals.length === 0) return null
      
      // Sort all vitals by recordedAt descending (newest first)
      const sortedVitals = [...allVitals].sort((a, b) => {
        const dateA = new Date(a.recordedAt).getTime()
        const dateB = new Date(b.recordedAt).getTime()
        return dateB - dateA // Descending order (newest first)
      })
      
      // Aggregate: find the latest value for each vital type across all records
      const aggregated: any = {
        id: sortedVitals[0].id, // Use the most recent record's ID
        patientId: consultation.patientId,
        recordedAt: sortedVitals[0].recordedAt, // Use the most recent timestamp
      }
      
      // Find latest blood pressure
      for (const vital of sortedVitals) {
        if (vital.bloodPressure && !aggregated.bloodPressure) {
          aggregated.bloodPressure = vital.bloodPressure
        }
      }
      
      // Find latest heart rate
      for (const vital of sortedVitals) {
        if (vital.heartRate && !aggregated.heartRate) {
          aggregated.heartRate = vital.heartRate
        }
      }
      
      // Find latest temperature
      for (const vital of sortedVitals) {
        if (vital.temperature && !aggregated.temperature) {
          aggregated.temperature = vital.temperature
        }
      }
      
      return aggregated
    },
    enabled: !!consultation?.patientId,
  })

  const createVitalsMutation = useMutation({
    mutationFn: (data: VitalSignsFormData) => vitalSignsService.create(data),
    onSuccess: async () => {
      // Invalidate all vital signs queries
      await queryClient.invalidateQueries({ queryKey: ['vital-signs', 'patient', consultation?.patientId], exact: false })
      await queryClient.invalidateQueries({ queryKey: ['vital-signs'], exact: false })
      // Refetch latest vitals to update the header display
      if (consultation?.patientId) {
        await refetchLatestVitals()
      }
      setShowVitalsForm(false)
    },
  })

  // Initialize form data from consultation (only on initial load per consultation)
  useEffect(() => {
    if (!consultation) return
    if (initializedConsultationIdRef.current === consultation.id) return

    initializedConsultationIdRef.current = consultation.id
    setNotes(consultation.notes || '')
    setNoteHighlights(consultation.noteHighlights || [])
    setDiagnosis(consultation.diagnosis || [])
    setCodes(consultation.codes || [])
    setPlan(consultation.plan || '')
    setReason(consultation.reason || '')
    lastSavedReasonRef.current = consultation.reason || ''
    setPatientInstructions(consultation.patientInstructions || '')
    setInstructionsGiven(consultation.instructionsGiven || false)
    setInstructionsGivenAt(consultation.instructionsGivenAt || '')
    setInstructionsGivenBy(consultation.instructionsGivenBy || '')
    
    // Initialize timer - calculate elapsed time from consultation start
    if (consultation.status === 'in-progress') {
      const startTime = new Date(consultation.createdAt).getTime()
      const now = Date.now()
      const initialElapsed = Math.floor((now - startTime) / 1000)
      setElapsedSeconds(initialElapsed)
    }
  }, [consultation])

  // Update Gemini context when patient/consultation data changes
  // Using sanitized utility to ensure no PII is included
  useEffect(() => {
    if (patient && consultation) {
      const context = buildConsultationContextWithNotes(
        patient,
        reason || consultation.reason,
        notes || consultation.notes,
        diagnosis.length > 0 ? diagnosis : consultation.diagnosis,
      )
      setConsultationContext(context)
    } else {
      setConsultationContext(null)
    }

    return () => {
      // Clear context when component unmounts
      setConsultationContext(null)
    }
  }, [patient, consultation, notes, reason, diagnosis, setConsultationContext])

  // Timer effect
  useEffect(() => {
    if (!consultation || consultation.status !== 'in-progress' || isTimerPaused) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
      }
      return
    }

    // Start timer
    timerIntervalRef.current = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1)
    }, 1000)

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
      }
    }
  }, [consultation, isTimerPaused])

  // Handle pause/resume
  const handleToggleTimer = () => {
    if (isTimerPaused) {
      // Resume - continue from current elapsed time
      setPausedAt(null)
      setIsTimerPaused(false)
    } else {
      // Pause - stop the timer
      setPausedAt(Date.now())
      setIsTimerPaused(true)
    }
  }

  // Format time as HH:MM:SS
  const formatTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  // Auto-save notes
  useEffect(() => {
    if (!consultation || consultation.status !== 'in-progress') return

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }

    // Set new timeout for auto-save (2 seconds after last change)
    autoSaveTimeoutRef.current = setTimeout(() => {
      if (notes !== consultation.notes) {
        handleAutoSave()
      }
    }, 2000)

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [notes, noteHighlights, consultation])

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Consultation>) => consultationService.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultation', id] })
      queryClient.invalidateQueries({ queryKey: ['consultations'] })
      setIsSaving(false)
    },
    onError: () => {
      setIsSaving(false)
      alert('Failed to save changes')
    },
  })

  // Auto-save reason for visit (debounced)
  useEffect(() => {
    if (!consultation || consultation.status !== 'in-progress') return
    if (reason === lastSavedReasonRef.current) return

    if (reasonSaveTimeoutRef.current) {
      clearTimeout(reasonSaveTimeoutRef.current)
    }

    reasonSaveTimeoutRef.current = setTimeout(() => {
      if (reason !== lastSavedReasonRef.current) {
        lastSavedReasonRef.current = reason
        setIsSaving(true)
        updateMutation.mutate({ reason })
      }
    }, 600)

    return () => {
      if (reasonSaveTimeoutRef.current) {
        clearTimeout(reasonSaveTimeoutRef.current)
      }
    }
  }, [reason, consultation, updateMutation])

  const completeMutation = useMutation({
    mutationFn: () =>
      consultationService.complete(id!, {
        notes,
        diagnosis,
        plan,
        codes,
        patientInstructions,
        instructionsGiven,
        instructionsGivenAt,
        instructionsGivenBy,
      }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['consultation', id] })
      queryClient.invalidateQueries({ queryKey: ['consultations'] })
      // Refetch appointments to reflect status change to 'completed'
      if (consultation?.appointmentId) {
        await queryClient.refetchQueries({ queryKey: ['appointments'] })
      }
      if (consultation?.patientId) {
        navigate(`/patients/${consultation.patientId}`)
      }
    },
  })

  const handleAutoSave = async () => {
    if (!consultation || consultation.status !== 'in-progress') return
    setIsSaving(true)
    try {
      await updateMutation.mutateAsync({
        notes,
        noteHighlights,
        diagnosis,
        plan,
        reason,
        codes,
        patientInstructions,
        instructionsGiven,
        instructionsGivenAt,
        instructionsGivenBy,
      })
    } catch (error) {
      console.error('Auto-save failed:', error)
    }
  }

  const handleComplete = async () => {
    if (window.confirm('Complete this consultation? This will set the status to completed.')) {
      try {
        await completeMutation.mutateAsync()
      } catch (error: any) {
        alert(error?.message || 'Failed to complete consultation')
      }
    }
  }

  const handleAddCode = (result: TerminologySearchResult) => {
    if (!consultation || consultation.status !== 'in-progress') return
    const now = new Date().toISOString()
    const newCode: ConsultationCode = {
      system: result.system,
      code: result.code,
      display: result.display,
      version: result.version,
      createdAt: now,
    }
    const existing = codes || []
    if (existing.some((c) => c.system === newCode.system && c.code === newCode.code)) {
      return
    }
    const updatedCodes = [...existing, newCode]
    setCodes(updatedCodes)
    // Persist structured codes with the consultation
    updateMutation.mutate({ codes: updatedCodes })
    setCodeSearch('')
    setCodeResults([])
  }

  const handleRemoveCode = (index: number) => {
    const existing = codes || []
    const updated = [...existing]
    updated.splice(index, 1)
    setCodes(updated)
    if (consultation && consultation.status === 'in-progress') {
      updateMutation.mutate({ codes: updated })
    }
  }

  // Add condition mutation
  const addConditionMutation = useMutation({
    mutationFn: ({ code, result }: { code: ConsultationCode; result?: TerminologySearchResult }) => {
      if (!consultation?.patientId) throw new Error('Patient ID required')
      const conditionData = {
        condition: code.display,
        status: 'active' as const,
        icd10Code: result?.icd10Mappings?.[0] || undefined,
        notes: `Added from consultation - ${code.system}:${code.code}`,
      }
      return patientHistoryService.addCondition(consultation.patientId, conditionData)
    },
    onSuccess: () => {
      if (consultation?.patientId) {
        queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', consultation.patientId] })
      }
    },
  })

  // Remove condition mutation
  const removeConditionMutation = useMutation({
    mutationFn: (conditionId: string) => {
      if (!consultation?.patientId) throw new Error('Patient ID required')
      return patientHistoryService.removeCondition(consultation.patientId, conditionId)
    },
    onSuccess: () => {
      if (consultation?.patientId) {
        queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', consultation.patientId] })
      }
    },
  })

  const handleAddAsCondition = async (code: ConsultationCode, _index: number) => {
    let result: TerminologySearchResult | undefined
    if (codeResults.length > 0) {
      result = codeResults.find(
        (r) => r.system === code.system && r.code === code.code
      )
    }
    
    if (!result) {
      try {
        const searchResults = await terminologyService.search(code.system, code.code, { limit: 1 })
        result = searchResults.find((r) => r.system === code.system && r.code === code.code)
      } catch (error) {
        console.warn('Could not fetch code details for condition', error)
      }
    }
    
    try {
      await addConditionMutation.mutateAsync({ code, result })
    } catch (error) {
      console.error('Failed to add condition', error)
      alert('Failed to add condition to clinical history')
    }
  }

  const handleRemoveAsCondition = async (conditionId: string) => {
    try {
      await removeConditionMutation.mutateAsync(conditionId)
    } catch (error) {
      console.error('Failed to remove condition', error)
      alert('Failed to remove condition from clinical history')
    }
  }

  const handleSearchCodes = async (query: string) => {
    setCodeSearch(query)
    if (!query.trim()) {
      setCodeResults([])
      return
    }
    try {
      setIsSearchingCodes(true)
      const results = await terminologyService.search(selectedSystem, query, { limit: 20 })
      setCodeResults(results)
    } catch (error) {
      console.error('Failed to search terminology codes', error)
    } finally {
      setIsSearchingCodes(false)
    }
  }

  const handleQuickAction = (category: TemplateCategory) => {
    if (category === 'referral') {
      // Navigate to create referral with consultation context
      if (consultation?.patientId) {
        navigate(`/referrals/new?patientId=${consultation.patientId}&consultationId=${consultation.id}`)
      }
    } else {
      setSelectedCategory(category)
      setShowTemplateModal(true)
    }
  }

  const handleGenerateDocument = (templateId: string) => {
    setSelectedTemplateId(templateId)
    setShowTemplateModal(false)
  }

  const handleSuggestionClick = useCallback((suggestion: AISuggestion) => {
    if (!consultation) return
    
    // Handle different suggestion types
    switch (suggestion.type) {
      case 'diagnosis':
        if (!diagnosis.includes(suggestion.title)) {
          const updated = [...diagnosis, suggestion.title]
          setDiagnosis(updated)
          if (consultation.status === 'in-progress') {
            updateMutation.mutate({ diagnosis: updated })
          }
        }
        break
      case 'treatment':
      case 'medication':
        // Add to treatment plan
        const currentPlan = plan || consultation?.plan || ''
        const newPlan = currentPlan
          ? `${currentPlan}\n\n- ${suggestion.title}: ${suggestion.description}`
          : `${suggestion.title}: ${suggestion.description}`
        setPlan(newPlan)
        if (consultation.status === 'in-progress') {
          updateMutation.mutate({ plan: newPlan })
        }
        break
      case 'test':
      case 'referral':
      case 'followup':
        // Add to notes
        const currentNotes = notes || consultation?.notes || ''
        const newNotes = currentNotes
          ? `${currentNotes}\n\n[AI Suggestion] ${suggestion.title}: ${suggestion.description}`
          : `[AI Suggestion] ${suggestion.title}: ${suggestion.description}`
        setNotes(newNotes)
        break
    }
  }, [consultation, diagnosis, plan, notes, updateMutation])

  // Listen for suggestion clicks from GeminiPanel
  useEffect(() => {
    const handleSuggestionEvent = (e: CustomEvent<AISuggestion>) => {
      handleSuggestionClick(e.detail)
    }
    window.addEventListener('gemini-suggestion-click', handleSuggestionEvent as EventListener)
    return () => {
      window.removeEventListener('gemini-suggestion-click', handleSuggestionEvent as EventListener)
    }
  }, [handleSuggestionClick])

  const shouldRedirect = consultation?.status && consultation.status !== 'in-progress'
  useEffect(() => {
    if (shouldRedirect) {
      navigate(`/consultations/${id}`)
    }
  }, [shouldRedirect, navigate, id])

  if (isLoading) return <div className="loading">Loading consultation...</div>
  if (error) return <div className="error">Error loading consultation</div>
  if (!consultation) return <div>Consultation not found</div>
  if (shouldRedirect) {
    return null
  }

  const getStatusBadge = (status: Consultation['status']) => {
    const statusClasses = {
      'in-progress': 'status-in-progress',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
      'no-show': 'status-no-show',
    }
    return <span className={`status-badge ${statusClasses[status]}`}>{status}</span>
  }

  return (
    <div className="consultation-workspace">
      <div className="workspace-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate(`/patients/${consultation.patientId}`)}>
            ← Back to Patient
          </button>
          <div>
            <h1>Consultation Workspace</h1>
            <div className="consultation-meta">
              <span className="consultation-date">
                {formatDate(consultation.consultationDate)}
                {consultation.startTime && ` at ${consultation.startTime}`}
              </span>
              {getStatusBadge(consultation.status)}
              {patient && (
                <span className="patient-name">
                  {patient.firstName} {patient.lastName}
                </span>
              )}
              <div className="consultation-timer">
                <span className="timer-display">{formatTime(elapsedSeconds)}</span>
                <button
                  className="timer-control-btn"
                  onClick={handleToggleTimer}
                  title={isTimerPaused ? 'Resume timer' : 'Pause timer'}
                  aria-label={isTimerPaused ? 'Resume timer' : 'Pause timer'}
                >
                  {isTimerPaused ? (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="white">
                      <path d="M2 2L10 6L2 10V2Z" />
                    </svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="white">
                      <rect x="3" y="2" width="2" height="8" />
                      <rect x="7" y="2" width="2" height="8" />
                    </svg>
                  )}
                </button>
              </div>
              {isSaving && <span className="saving-indicator">Saving...</span>}
              {/* Vital Signs Summary */}
              {consultation?.patientId && (
                <div className="vitals-summary-header">
                  {latestVitals ? (
                    <>
                      {latestVitals.bloodPressure && (
                        <span className="vital-item">
                          BP: {latestVitals.bloodPressure.systolic}/{latestVitals.bloodPressure.diastolic}
                        </span>
                      )}
                      {latestVitals.heartRate?.value && (
                        <span className="vital-item">HR: {latestVitals.heartRate.value}</span>
                      )}
                      {latestVitals.temperature?.value && (
                        <span className="vital-item">
                          Temp: {(() => {
                            const unitPref = getUnitPreference()
                            const temp = unitPref === 'imperial'
                              ? (latestVitals.temperature.value * 9) / 5 + 32
                              : latestVitals.temperature.value
                            return `${temp.toFixed(1)}°${unitPref === 'imperial' ? 'F' : 'C'}`
                          })()}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="vital-item no-vitals">No vitals recorded</span>
                  )}
                  <div className="vitals-actions">
                    <button
                      className="btn-vital-action"
                      onClick={() => setShowVitalsForm(true)}
                      title="Add Vital Signs"
                      aria-label="Add Vital Signs"
                    >
                      + Add
                    </button>
                    <button
                      className="btn-vital-action btn-vital-icon"
                      onClick={() => setShowVitalsCharts(true)}
                      title="View Charts"
                      aria-label="View Charts"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 6 13.5 14.5 8.5 9.5 2 16"></polyline>
                        <polyline points="16 6 22 6 22 12"></polyline>
                      </svg>
                    </button>
                    <button
                      className="btn-vital-action btn-vital-icon"
                      onClick={() => setShowVitalsTable(true)}
                      title="View Table"
                      aria-label="View Table"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => navigate(`/consultations/${id}`)}>
            View Details
          </button>
          <button
            className="btn-primary"
            onClick={handleComplete}
            disabled={completeMutation.isPending}
          >
            {completeMutation.isPending ? 'Completing...' : 'Complete Consultation'}
          </button>
        </div>
      </div>

      {/* AI Suggestions are now handled by GeminiPanel via context */}

      <div className="workspace-content">
        {/* Quick Actions - Left Vertical Panel */}
        <div className="quick-actions-section">
          <h2>Quick Actions</h2>
          <div className="quick-actions-grid">
            <button
              className="quick-action-btn"
              onClick={() => handleQuickAction('letter')}
              title="Create Letter"
            >
              <span className="action-icon">
                <svg className="action-svg-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </span>
              <span className="action-label">Letter</span>
            </button>
            <button
              className="quick-action-btn"
              onClick={() => handleQuickAction('referral')}
              title="Create Referral"
            >
              <span className="action-icon">
                <svg className="action-svg-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
              </span>
              <span className="action-label">Referral</span>
            </button>
            <button
              className="quick-action-btn"
              onClick={() => handleQuickAction('prescription')}
              title="Create Prescription"
            >
              <span className="action-icon">
                <svg className="action-svg-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="9" y1="3" x2="9" y2="21"></line>
                  <line x1="3" y1="9" x2="21" y2="9"></line>
                  <circle cx="15" cy="15" r="2"></circle>
                </svg>
              </span>
              <span className="action-label">Prescription</span>
            </button>
            <button
              className="quick-action-btn"
              onClick={() => handleQuickAction('certificate')}
              title="Create Certificate"
            >
              <span className="action-icon">
                <svg className="action-svg-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </span>
              <span className="action-label">Certificate</span>
            </button>
            <button
              className="quick-action-btn"
              onClick={() => handleQuickAction('form')}
              title="Create Form"
            >
              <span className="action-icon">
                <svg className="action-svg-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </span>
              <span className="action-label">Form</span>
            </button>
            <button
              className="quick-action-btn"
              onClick={() => setShowTemplateModal(true)}
              title="Other Templates"
            >
              <span className="action-icon">
                <svg className="action-svg-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                </svg>
              </span>
              <span className="action-label">More...</span>
            </button>
          </div>
        </div>

        <div className="workspace-main">
          {/* Reason for Visit */}
          <div className="workspace-section">
            <label htmlFor="reason">Reason for Visit</label>
            <input
              id="reason"
              type="text"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value)
              }}
              placeholder="e.g., Annual checkup, chest pain, follow-up"
              className="reason-input"
            />
          </div>

          {/* Notes Editor with inline autocomplete */}
          <div className="workspace-section notes-section">
            <div className="section-header">
              <label htmlFor="notes">Clinical Notes</label>
              <span className="section-hint">Auto-saves as you type • Tab/Enter to accept suggestions</span>
            </div>
            <ConsultationNoteEditor
              value={notes}
              onChange={setNotes}
              highlights={noteHighlights}
              onHighlightsChange={setNoteHighlights}
              sectionType="notes"
              terminologySystem={selectedSystem}
              reasonForVisit={reason}
              diagnosis={diagnosis}
            />
          </div>

          {/* Treatment Plan */}
          <div className="workspace-section">
            <label htmlFor="plan">Treatment Plan</label>
            <textarea
              id="plan"
              value={plan}
              onChange={(e) => {
                setPlan(e.target.value)
                if (consultation.status === 'in-progress') {
                  updateMutation.mutate({ plan: e.target.value })
                }
              }}
              placeholder="Enter treatment plan, medications, referrals, follow-up instructions..."
              className="plan-editor"
              rows={6}
            />
          </div>

          {/* Patient Instructions */}
          <div className="workspace-section">
            <label htmlFor="patientInstructions">Patient Instructions</label>
            <textarea
              id="patientInstructions"
              value={patientInstructions}
              onChange={(e) => {
                setPatientInstructions(e.target.value)
                if (consultation.status === 'in-progress') {
                  updateMutation.mutate({ patientInstructions: e.target.value })
                }
              }}
              placeholder="Enter patient-specific instructions..."
              className="plan-editor"
              rows={4}
            />
            <div style={{ marginTop: '0.75rem' }}>
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={instructionsGiven}
                  onChange={(e) => {
                    const currentUser = authService.getCurrentUser()
                    const isGiven = e.target.checked
                    setInstructionsGiven(isGiven)
                    setInstructionsGivenAt(isGiven ? new Date().toISOString() : '')
                    setInstructionsGivenBy(isGiven && currentUser ? currentUser.id : '')
                    if (consultation.status === 'in-progress') {
                      updateMutation.mutate({
                        instructionsGiven: isGiven,
                        instructionsGivenAt: isGiven ? new Date().toISOString() : '',
                        instructionsGivenBy: isGiven && currentUser ? currentUser.id : '',
                      })
                    }
                  }}
                />
                <span>Instructions Given to Patient</span>
              </label>
            </div>
          </div>
        </div>

        <div className="workspace-sidebar">
          {/* Patient Info */}
          {patient && (
            <div className="sidebar-section">
              <h3>Patient</h3>
              <div className="patient-info-card">
                <div className="patient-name-large">
                  {patient.firstName} {patient.lastName}
                </div>
                {patient.dateOfBirth && (
                  <div className="patient-detail">
                    DOB: {formatDate(patient.dateOfBirth)}
                  </div>
                )}
                {patient.patientNumber && (
                  <div className="patient-detail">
                    Patient #: {patient.patientNumber}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Diagnosis card – under patient card */}
          <div className="sidebar-section">
            <h3>Diagnosis</h3>
            {/* Coded entry control */}
            <div className="coded-diagnosis-header">
              <div className="coded-diagnosis-system-toggle">
                <label htmlFor="sidebar-terminologySystem">Terminology</label>
                <select
                  id="sidebar-terminologySystem"
                  value={selectedSystem}
                  onChange={(e) =>
                    setSelectedSystem(e.target.value as ConsultationTerminologySystem)
                  }
                >
                  <option value="ICPC2">ICPC-2</option>
                  <option value="ICD10">ICD-10</option>
                </select>
              </div>
            </div>
            <div className="diagnosis-input-group">
              <input
                type="text"
                value={codeSearch}
                onChange={(e) => handleSearchCodes(e.target.value)}
                placeholder={`Search ${selectedSystem} codes...`}
                className="diagnosis-input"
              />
            </div>
            {isSearchingCodes && <div className="coded-diagnosis-loading">Searching codes...</div>}
            {codeResults.length > 0 && (
              <div className="coded-diagnosis-results">
                {codeResults.map((result) => (
                  <button
                    key={`${result.system}-${result.code}`}
                    type="button"
                    className="coded-diagnosis-result"
                    onClick={() => handleAddCode(result)}
                  >
                    <span className="coded-diagnosis-code">
                      {result.system}:{result.code}
                    </span>
                    <span className="coded-diagnosis-display">{result.display}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Summary chips */}
            {(diagnosis.length > 0 || (codes && codes.length > 0)) && (
              <div className="diagnosis-summary">
                {diagnosis.length > 0 && (
                  <div className="diagnosis-list">
                    {diagnosis.map((diag, index) => (
                      <span key={index} className="diagnosis-tag">
                        {diag}
                      </span>
                    ))}
                  </div>
                )}
                {codes && codes.length > 0 && (
                  <div className="diagnosis-list coded-diagnosis-list">
                    {codes.map((code, index) => {
                      const conditionId = isCodeAddedAsCondition(code)
                      const isAdded = !!conditionId
                      return (
                        <span
                          key={`${code.system}-${code.code}-${index}`}
                          className={`diagnosis-tag ${isAdded ? 'diagnosis-tag-condition' : ''}`}
                        >
                          {code.code} – {code.display}
                          <button
                            type="button"
                            className="diagnosis-add-condition"
                            onClick={(e) => {
                              e.stopPropagation()
                              if (isAdded && conditionId) {
                                handleRemoveAsCondition(conditionId)
                              } else {
                                handleAddAsCondition(code, index)
                              }
                            }}
                            disabled={addConditionMutation.isPending || removeConditionMutation.isPending}
                            title={isAdded ? 'Remove from Clinical Conditions' : 'Add to Clinical Conditions'}
                            aria-label={isAdded ? 'Remove from Clinical Conditions' : 'Add to Clinical Conditions'}
                          >
                            {isAdded ? (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 14 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M11.6667 3.5L5.25 9.91667L2.33334 7"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            ) : (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 14 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M7 2.33334V11.6667M2.33334 7H11.6667"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            )}
                          </button>
                          <button
                            type="button"
                            className="diagnosis-remove"
                            onClick={() => handleRemoveCode(index)}
                          >
                            ×
                          </button>
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Medications */}
          {patient && (
            <div className="sidebar-section">
              <div className="section-header">
                <h3>Medications</h3>
              </div>
              <MedicationsCard patientId={patient.id} />
            </div>
          )}

          {/* Consultation Details */}
          <div className="sidebar-section">
            <h3>Consultation Details</h3>
            <div className="detail-list">
              {consultation.staffName && (
                <div className="detail-item">
                  <label>Provider</label>
                  <p>{consultation.staffName}</p>
                </div>
              )}
              <div className="detail-item">
                <label>Status</label>
                <p>{getStatusBadge(consultation.status)}</p>
              </div>
              {consultation.duration && (
                <div className="detail-item">
                  <label>Duration</label>
                  <p>{consultation.duration} minutes</p>
                </div>
              )}
            </div>
          </div>

          {/* Artefacts */}
          <div className="sidebar-section">
            <div className="section-header">
              <h3>Documents & Artefacts</h3>
            </div>
            {(() => {
              // Collect all artefacts from current and past consultations
              const currentArtefacts = consultation.artefacts || []
              const pastConsultations = allConsultations?.filter(c => c.id !== consultation.id && c.artefacts && c.artefacts.length > 0) || []
              
              const hasAnyArtefacts = currentArtefacts.length > 0 || pastConsultations.length > 0

              if (!hasAnyArtefacts) {
                return (
                  <div className="empty-state-small">
                    <p>No documents yet</p>
                    <p className="empty-hint">Use quick actions to create documents</p>
                  </div>
                )
              }

              return (
                <div className="artefacts-container-workspace">
                  {currentArtefacts.length > 0 && (
                    <div className="artefacts-group">
                      <div className="artefacts-group-label">This Consultation</div>
                      <ConsultationArtefactList
                        consultationId={consultation.id}
                        artefacts={currentArtefacts}
                      />
                    </div>
                  )}
                  {pastConsultations.length > 0 && (
                    <div className="artefacts-group past-artefacts">
                      <div className="artefacts-group-label">Past Consultations</div>
                      {pastConsultations.map((pastConsultation) => (
                        <div key={pastConsultation.id} className="past-consultation-artefacts">
                          <div className="past-consultation-date">
                            {formatDate(pastConsultation.consultationDate)}
                            {pastConsultation.startTime && ` at ${pastConsultation.startTime}`}
                          </div>
                          <ConsultationArtefactList
                            consultationId={pastConsultation.id}
                            artefacts={pastConsultation.artefacts || []}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })()}
          </div>
        </div>
      </div>

      <ConsultationWorkspaceTabs
        patient={patient}
        consultation={consultation}
        allConsultations={allConsultations}
        selectedSystem={selectedSystem}
        onSystemChange={setSelectedSystem}
        codeSearch={codeSearch}
        onCodeSearch={handleSearchCodes}
        codeResults={codeResults}
        isSearchingCodes={isSearchingCodes}
        onAddCode={handleAddCode}
        onRemoveCode={handleRemoveCode}
        diagnosis={diagnosis}
        codes={codes}
        isCodeAddedAsCondition={isCodeAddedAsCondition}
        onAddAsCondition={handleAddAsCondition}
        onRemoveAsCondition={handleRemoveAsCondition}
        addConditionMutationPending={addConditionMutation.isPending}
        removeConditionMutationPending={removeConditionMutation.isPending}
      />

      {/* Template Selection Modal */}
      <Modal
        isOpen={showTemplateModal}
        onClose={() => {
          setShowTemplateModal(false)
          setSelectedCategory('all')
        }}
        title="Select Template"
        size="large"
      >
        <div className="template-selector">
          <div className="template-category-filter">
            <label>Category:</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as TemplateCategory | 'all')}
            >
              <option value="all">All Categories</option>
              <option value="letter">Letters</option>
              <option value="referral">Referrals</option>
              <option value="prescription">Prescriptions</option>
              <option value="certificate">Certificates</option>
              <option value="form">Forms</option>
              <option value="report">Reports</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="template-list">
            {templates && templates.length > 0 ? (
              templates.map((template) => (
                <button
                  key={template.id}
                  className="template-card-select"
                  onClick={() => handleGenerateDocument(template.id)}
                >
                  <div className="template-card-header">
                    <h4>{template.name}</h4>
                    {template.category && (
                      <span className="template-category-badge">{template.category}</span>
                    )}
                  </div>
                  {template.description && (
                    <p className="template-description">{template.description}</p>
                  )}
                </button>
              ))
            ) : (
              <div className="empty-state">
                <p>No templates found in this category.</p>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Document Generation Modal */}
      {selectedTemplateId && consultation && (
        <GenerateDocumentModal
          templateId={selectedTemplateId}
          patientId={consultation.patientId}
          consultationId={consultation.id}
          onClose={() => {
            setSelectedTemplateId(null)
            queryClient.invalidateQueries({ queryKey: ['consultation', id] })
            queryClient.invalidateQueries({ queryKey: ['consultations', 'patient', consultation.patientId] })
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['consultation', id] })
            queryClient.invalidateQueries({ queryKey: ['consultations', 'patient', consultation.patientId] })
          }}
        />
      )}

      {/* Vital Signs Form Modal */}
      {consultation && consultation.patientId && showVitalsForm && (
        <Modal
          isOpen={showVitalsForm}
          onClose={() => setShowVitalsForm(false)}
          title="Add Vital Signs"
          size="large"
        >
          <VitalSignsForm
            patientId={consultation.patientId}
            consultationId={consultation.id}
            patient={patient || undefined}
            onSubmit={async (data) => {
              await createVitalsMutation.mutateAsync(data)
            }}
            onCancel={() => setShowVitalsForm(false)}
            userPreference={getUnitPreference()}
          />
        </Modal>
      )}

      {/* Vital Signs Charts Dialog */}
      {consultation && consultation.patientId && (
        <VitalSignsChartsDialog
          patientId={consultation.patientId}
          patient={patient || undefined}
          userPreference={getUnitPreference()}
          isOpen={showVitalsCharts}
          onClose={() => setShowVitalsCharts(false)}
        />
      )}

      {/* Vital Signs Table Dialog */}
      {consultation && consultation.patientId && (
        <VitalSignsTableDialog
          patientId={consultation.patientId}
          consultationId={consultation.id}
          userPreference={getUnitPreference()}
          isOpen={showVitalsTable}
          onClose={() => setShowVitalsTable(false)}
        />
      )}
    </div>
  )
}












